import { fetchAuthSession } from 'aws-amplify/auth'; // Import fetchAuthSession
import { Download, Search, X } from 'lucide-react';
import React, { useEffect, useMemo, useState } from 'react';
import { useScreenLogging } from '../../hooks/useScreenLogging';
import loggingService from '../../services/loggingService';
import {
    clampDateToToday,
    getLastWeekStartISODate,
    getTodayISODate,
    getWeekStartISODate,
} from '../../utils/dateHelpers';

const FieldOfficerReport = () => {
  // Log screen view
  useScreenLogging('FieldOfficerReport', {
    component: 'FieldOfficerReport',
  });
    const [selectedStartDate, setSelectedStartDate] = useState(() => getLastWeekStartISODate()); // Week start (YYYY-MM-DD)
    const [error, setError] = useState(null);
    const [officers, setOfficers] = useState([]);
    const [filteredOfficers, setFilteredOfficers] = useState([]);
    const [loadingOfficers, setLoadingOfficers] = useState(false);
    const [officerError, setOfficerError] = useState(null);
    const [isSearchExpanded, setIsSearchExpanded] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [downloadState, setDownloadState] = useState({
        officerId: null,
        isLoading: false,
    });
    const [toastMessage, setToastMessage] = useState('');

    useEffect(() => {
        if (!toastMessage) return;
        const timer = setTimeout(() => setToastMessage(''), 3000);
        return () => clearTimeout(timer);
    }, [toastMessage]);

    const BASE_URL = 'https://api.innoworx.ai/Stage'; // From Reports API Documentation

    // Cache configuration
    const CACHE_CONFIG = {
        REPORTS_TTL: 30 * 60 * 1000, // 30 minutes for field officer reports
        CACHE_PREFIX: 'fieldOfficerReports_cache_',
    };

    // Cache utility functions
    const getCacheKey = (type, ...params) => {
        return `${CACHE_CONFIG.CACHE_PREFIX}${type}_${params.join('_')}`;
    };

    const getCachedData = (cacheKey) => {
        try {
            const cached = localStorage.getItem(cacheKey);
            if (!cached) return null;

            const { data, timestamp, ttl } = JSON.parse(cached);
            const now = Date.now();
            
            // Check if cache is expired
            if (now - timestamp > ttl) {
                localStorage.removeItem(cacheKey);
                return null;
            }

            return data;
        } catch (error) {
            console.error('Error reading from cache:', error);
            localStorage.removeItem(cacheKey);
            return null;
        }
    };

    const setCachedData = (cacheKey, data, ttl) => {
        try {
            const cacheEntry = {
                data,
                timestamp: Date.now(),
                ttl,
            };
            localStorage.setItem(cacheKey, JSON.stringify(cacheEntry));
        } catch (error) {
            console.error('Error writing to cache:', error);
            // If storage is full, try to clear old cache entries
            clearExpiredCache();
        }
    };

    const clearExpiredCache = () => {
        try {
            const keys = Object.keys(localStorage);
            keys.forEach(key => {
                if (key.startsWith(CACHE_CONFIG.CACHE_PREFIX)) {
                    const cached = localStorage.getItem(key);
                    if (cached) {
                        try {
                            const { timestamp, ttl } = JSON.parse(cached);
                            if (Date.now() - timestamp > ttl) {
                                localStorage.removeItem(key);
                            }
                        } catch (e) {
                            // Invalid cache entry, remove it
                            localStorage.removeItem(key);
                        }
                    }
                }
            });
        } catch (error) {
            console.error('Error clearing expired cache:', error);
        }
    };

    // Cleanup expired cache entries on mount
    useEffect(() => {
        clearExpiredCache();
    }, []);

    // Function to get the authentication token
    const getAuthToken = async () => {
        try {
            const { tokens } = await fetchAuthSession();
            if (tokens && tokens.idToken) {
                return tokens.idToken.toString();
            } else {
                console.error('No ID Token found in the current session.');
                return null;
            }
        } catch (error) {
            console.error('Error fetching authentication session:', error);
            return null;
        }
    };

    // Function to format date for display (DD/MM/YYYY)
    const formatDate = (dateString) => {
        if (!dateString) return '';
        const d = new Date(dateString);
        const day = String(d.getDate()).padStart(2, '0');
        const month = String(d.getMonth() + 1).padStart(2, '0');
        const year = d.getFullYear();
        return `${day}/${month}/${year}`;
    };

    const getWeekEndDate = (dateString) => {
        if (!dateString) return '';
        const d = new Date(dateString);
        d.setDate(d.getDate() + 6);
        return d.toISOString().split('T')[0];
    };

    useEffect(() => {
        const fetchOfficers = async () => {
            setLoadingOfficers(true);
            setOfficerError(null);
            try {
                const token = await getAuthToken();
                if (!token) {
                    throw new Error('Authentication token is missing. Please ensure a user is logged in.');
                }

                const response = await fetch(`${BASE_URL}/report/officer/getall-users-by-role`, {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`,
                    },
                });

                if (!response.ok) {
                    const errorText = await response.text();
                    throw new Error(`Failed to load officers. Status: ${response.status}, message: ${errorText}`);
                }

                const data = await response.json();
                if (Array.isArray(data)) {
                    setOfficers(data);
                    
                    // Log API event for fetching officers
                    loggingService.logInfo(
                        'api_success_field_officer_report',
                        `Successfully fetched officers list`,
                        {
                            endpoint: '/report/officer/getall-users-by-role',
                            method: 'GET',
                            officersCount: data.length,
                        }
                    ).catch(logError => {
                        console.warn('[FieldOfficerReport] Failed to log event:', logError);
                    });
                } else {
                    console.warn('Unexpected officer directory response:', data);
                    setOfficers([]);
                }
            } catch (err) {
                console.error('Error fetching officers for field officer reports:', err);
                setOfficerError(err?.message || 'Failed to load officer directory.');
                setOfficers([]);
            } finally {
                setLoadingOfficers(false);
            }
        };

        fetchOfficers();
    }, [BASE_URL]);

    useEffect(() => {
        if (!Array.isArray(officers) || officers.length === 0) {
            setFilteredOfficers([]);
            return;
        }

        if (!searchQuery.trim()) {
            setFilteredOfficers(officers);
            return;
        }

        const query = searchQuery.toLowerCase();
        const filtered = officers.filter(officer => {
            const name = (officer.user_name || officer.name || '').toLowerCase();
            const employeeId = (officer.employee_id || officer.user_id || '').toLowerCase();
            const role = (officer.spec_role || officer.role || '').toLowerCase();
            return (
                name.includes(query) ||
                employeeId.includes(query) ||
                role.includes(query)
            );
        });

        setFilteredOfficers(filtered);
    }, [officers, searchQuery]);

    const fetchReportForOfficer = async officerId => {
        if (!selectedStartDate) {
            throw new Error('Please select a week start date.');
        }
        if (!officerId) {
            throw new Error('Officer ID is missing.');
        }

        const derivedEndDate = getWeekEndDate(selectedStartDate);
        const cacheKey = getCacheKey('officer', officerId, selectedStartDate, derivedEndDate);

        const cached = getCachedData(cacheKey);
        if (cached) {
            return cached;
        }

        const token = await getAuthToken();
        if (!token) {
            throw new Error('Authentication token is missing. Please ensure a user is logged in.');
        }

        const headers = {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
        };

        const buildUrl = params => `${BASE_URL}/report/officer?${params.toString()}`;
        const weekParams = new URLSearchParams({
            user_id: officerId,
            week_start_date: selectedStartDate,
        });
        const rangeParams = derivedEndDate
            ? new URLSearchParams({
                  user_id: officerId,
                  start_date: selectedStartDate,
                  end_date: derivedEndDate,
              })
            : null;

        let response = await fetch(buildUrl(weekParams), {
            method: 'GET',
            headers,
        });

        if (!response.ok && [400, 422].includes(response.status) && rangeParams) {
            const fallbackResponse = await fetch(buildUrl(rangeParams), {
                method: 'GET',
                headers,
            });
            response = fallbackResponse;
        }

        if (response.status === 404) {
            const emptyData = { notFound: true };
            setCachedData(cacheKey, emptyData, CACHE_CONFIG.REPORTS_TTL);
            return emptyData;
        }

        if (!response.ok) {
            const errorText = await response.text();
            throw new Error(`HTTP error! status: ${response.status}, message: ${errorText}`);
        }

        const data = await response.json();
        if (!data?.url) {
            const emptyData = { notFound: true };
            setCachedData(cacheKey, emptyData, CACHE_CONFIG.REPORTS_TTL);
            return emptyData;
        }

        setCachedData(cacheKey, data, CACHE_CONFIG.REPORTS_TTL);
        
        // Log API event for fetching officer report
        loggingService.logInfo(
            'api_success_field_officer_report',
            `Successfully fetched field officer report for officer ${officerId}`,
            {
                endpoint: '/report/officer',
                method: 'GET',
                officerId,
                weekStartDate: selectedStartDate,
                weekEndDate: derivedEndDate,
            }
        ).catch(logError => {
            console.warn('[FieldOfficerReport] Failed to log event:', logError);
        });
        
        return data;
    };

    const handleDownloadReport = async officer => {
        const officerId =
            officer?.user_id ||
            officer?.userId ||
            officer?.employee_id ||
            officer?.id;

        setError(null);

        if (!officerId) {
            setError('Unable to determine officer ID for this record.');
            return;
        }

        setDownloadState({
            officerId,
            isLoading: true,
        });

        try {
            const reportData = await fetchReportForOfficer(officerId);

            if (reportData?.notFound) {
                setToastMessage('No report found for the selected week.');
                return;
            }

            window.open(reportData.url, '_blank', 'noopener,noreferrer');
            setToastMessage('Report download started in a new tab.');
            
            // Log API event for viewing field officer report
            const derivedEndDate = getWeekEndDate(selectedStartDate);
            loggingService.logInfo(
                'report_view_field_officer',
                `User viewed field officer report for officer ${officerId} for week starting ${selectedStartDate}`,
                {
                    endpoint: '/report/officer',
                    method: 'GET',
                    officerId: officerId,
                    weekStartDate: selectedStartDate,
                    weekEndDate: derivedEndDate,
                }
            ).catch(logError => {
                console.warn('[FieldOfficerReport] Failed to log event:', logError);
            });
        } catch (err) {
            console.error('Error downloading field officer report:', err);
            setError(err.message || 'Failed to fetch field officer report.');
        } finally {
            setDownloadState({
                officerId: null,
                isLoading: false,
            });
        }
    };

    const handleStartDateChange = (event) => {
        const rawValue = event.target.value;
        const safeValue = clampDateToToday(rawValue) || getLastWeekStartISODate();
        const normalizedWeekStart = getWeekStartISODate(safeValue) || getLastWeekStartISODate();
        setSelectedStartDate(normalizedWeekStart);
        setError(null);
    };

    const formattedRange = useMemo(() => {
        if (!selectedStartDate) return '';
        const endDate = getWeekEndDate(selectedStartDate);
        return `${formatDate(selectedStartDate)} - ${formatDate(endDate)}`;
    }, [selectedStartDate]);

    const handleOfficerCardClick = officer => {
        if (!selectedStartDate || downloadState.isLoading) return;
        handleDownloadReport(officer);
    };

    const handleOfficerCardKeyDown = (event, officer) => {
        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            handleOfficerCardClick(officer);
        }
    };

    return (
        <div className="field-officer-report-wrapper">
            <div className="field-officer-report-surface">
                <div className="mb-6">
                    <h1 className="text-black text-3xl font-bold font-['Figtree'] leading-10">Employee Presents Report</h1>
                </div>

                {toastMessage && (
                    <div className="mb-4 p-4 bg-blue-50 border border-blue-200 text-blue-800 rounded-lg">
                        {toastMessage}
                    </div>
                )}

                {error && (
                    <div className="mb-4 p-4 bg-red-50 border border-red-200 text-red-700 rounded-lg">
                        {error}
                    </div>
                )}

                <div className="week-search-row mb-6">
                    <div className="week-picker">
                        <label className="block mb-2 text-sm font-semibold text-neutral-700">Week Starting</label>
                        <div className="w-full max-w-xs">
                            <input
                                type="date"
                                className={`mb-3 block w-full h-14 pl-4 pr-3.5 py-2.5 bg-neutral-50/80 rounded-[10px] shadow-[0px_15px_30px_0px_rgba(0,0,0,0.10)] shadow-[0px_5px_20.100000381469727px_0px_rgba(255,255,255,0.10)] shadow-[inset_0px_4px_25px_0px_rgba(255,255,255,0.35)] outline outline-1 outline-offset-[-1px] backdrop-blur-[30px] text-lg font-bold font-['Figtree'] leading-snug ${
                                    selectedStartDate
                                        ? 'outline-green-700 text-green-700'
                                        : 'outline-white text-neutral-900'
                                }`}
                                value={selectedStartDate}
                                max={getTodayISODate()}
                                onChange={handleStartDateChange}
                            />
                        </div>
                        <p className="mt-2 text-sm text-neutral-600">
                            {formattedRange
                                ? `Reports will cover ${formattedRange}`
                                : 'Select a week start date to generate reports.'}
                        </p>
                    </div>
                    <div className="w-full">
                        {isSearchExpanded ? (
                            <div className="search-expansion">
                                <input
                                    type="text"
                                    value={searchQuery}
                                    onChange={e => setSearchQuery(e.target.value)}
                                    placeholder="Search officers by name or ID..."
                                    className="search-input"
                                />
                                <button
                                    type="button"
                                    onClick={() => {
                                        setIsSearchExpanded(false);
                                        setSearchQuery('');
                                    }}
                                    className="search-close-btn"
                                >
                                    <X className="w-4 h-4" />
                                    Close
                                </button>
                            </div>
                        ) : (
                            <button
                                type="button"
                                onClick={() => setIsSearchExpanded(true)}
                                className="search-toggle-btn"
                            >
                                <Search className="w-5 h-5" />
                                Search Officers
                            </button>
                        )}
                        <p className="mt-2 text-sm text-neutral-600">
                            {searchQuery
                                ? `Showing ${filteredOfficers.length} result${filteredOfficers.length === 1 ? '' : 's'}`
                                : 'Expand to quickly find an officer'}
                        </p>
                    </div>
                </div>

                <div className="flex items-center justify-between mb-4">
                    <h2 className="text-lg font-semibold text-neutral-800">Reports</h2>
                    <span className="text-sm text-neutral-500">
                        {filteredOfficers.length} officer{filteredOfficers.length === 1 ? '' : 's'}
                    </span>
                </div>

                <div className="report-list">
                    {loadingOfficers ? (
                        <div className="empty-state">Loading officer directory...</div>
                    ) : officerError ? (
                        <div className="empty-state text-red-500">{officerError}</div>
                    ) : filteredOfficers.length === 0 ? (
                        <div className="empty-state">No officers found for your search.</div>
                    ) : (
                        filteredOfficers.map((officer, index) => {
                            const officerId =
                                officer.user_id ||
                                officer.userId ||
                                officer.employee_id ||
                                officer.id ||
                                `officer-${index}`;
                            const isDownloading =
                                downloadState.isLoading && downloadState.officerId === officerId;

                            return (
                                <div
                                    key={officerId}
                                    className={`officer-card ${isDownloading || !selectedStartDate ? 'officer-card--disabled' : ''}`}
                                    role="button"
                                    tabIndex={0}
                                    onClick={() => handleOfficerCardClick(officer)}
                                    onKeyDown={event => handleOfficerCardKeyDown(event, officer)}
                                    aria-disabled={isDownloading || !selectedStartDate}
                                >
                                    <div className="officer-details">
                                        <div className="officer-name">
                                            {officer.user_name || 'Unknown Officer'}
                                        </div>
                                        <div className="officer-meta">
                                            {(officer.spec_role || 'Unknown Role')}{' '}
                                            (ID: {officer.employee_id || officer.user_id || 'N/A'})
                                        </div>
                                        <div className="officer-week">
                                            {formattedRange
                                                ? `Week: ${formattedRange}`
                                                : 'Select a week to enable downloads'}
                                        </div>
                                    </div>
                                    <button
                                        className={`download-btn ${
                                            isDownloading ? 'download-btn--loading' : ''
                                        }`}
                                        onClick={event => {
                                            event.stopPropagation();
                                            handleDownloadReport(officer);
                                        }}
                                        disabled={isDownloading || !selectedStartDate}
                                    >
                                        {isDownloading ? (
                                            <div className="loader"></div>
                                        ) : (
                                            <Download className="w-5 h-5 text-white" strokeWidth={2} />
                                        )}
                                    </button>
                                </div>
                            );
                        })
                    )}
                </div>
            </div>

            <style>{`
                .field-officer-report-wrapper {
                    width: 100%;
                    padding: 0;
                    display: flex;
                    justify-content: flex-start;
                }

                .field-officer-report-surface {
                    width: 100%;
                    max-width: 100%;
                    background: transparent;
                    border-radius: 0;
                    padding: 0;
                    box-shadow: none;
                }

                .week-search-row {
                    display: grid;
                    grid-template-columns: minmax(0, 2fr) minmax(0, 1fr);
                    gap: 1rem;
                    align-items: end;
                }

                @media (max-width: 768px) {
                    .week-search-row {
                        grid-template-columns: 1fr;
                    }
                }

                .week-picker {
                    width: 100%;
                }

                .search-toggle-btn,
                .search-input,
                .search-close-btn {
                    height: 56px;
                    border-radius: 16px;
                    border: none;
                }

                .search-toggle-btn {
                    width: 100%;
                    background: rgba(255, 255, 255, 0.9);
                    box-shadow: 0 12px 20px rgba(62, 78, 255, 0.08);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    gap: 0.75rem;
                    font-weight: 600;
                    color: #1e2a53;
                    transition: all 0.3s ease-in-out;
                    cursor: pointer;
                }

                .search-toggle-btn:hover {
                    transform: translateY(-1px);
                    box-shadow: 0 14px 24px rgba(62, 78, 255, 0.12);
                }

                .search-expansion {
                    display: flex;
                    gap: 0.75rem;
                    animation: expandSearch 0.3s ease-in-out;
                }

                @keyframes expandSearch {
                    from {
                        opacity: 0;
                        transform: translateY(-10px);
                    }
                    to {
                        opacity: 1;
                        transform: translateY(0);
                    }
                }

                .search-input {
                    flex: 1;
                    padding: 0 1rem;
                    background: rgba(255, 255, 255, 0.9);
                    box-shadow: 0 12px 20px rgba(62, 78, 255, 0.08);
                    font-size: 1rem;
                    color: #1e2a53;
                }

                .search-close-btn {
                    padding: 0 1.5rem;
                    background: rgba(255, 255, 255, 0.65);
                    font-weight: 600;
                    color: #4b5567;
                    display: flex;
                    align-items: center;
                    gap: 0.5rem;
                    box-shadow: 0 8px 16px rgba(79, 114, 205, 0.15);
                }

                .report-list {
                    max-height: 60vh;
                    overflow-y: auto;
                    display: flex;
                    flex-direction: column;
                    gap: 1rem;
                }

                .officer-card {
                    background: #ffffff;
                    border-radius: 16px;
                    padding: 1.25rem;
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    gap: 1rem;
                    box-shadow: 0 8px 20px rgba(15, 23, 42, 0.08);
                    border: 1px solid rgba(226, 232, 240, 0.9);
                    cursor: pointer;
                    transition: box-shadow 0.2s ease, transform 0.2s ease;
                }

                .officer-card:hover:not(.officer-card--disabled) {
                    box-shadow: 0 12px 24px rgba(15, 23, 42, 0.12);
                    transform: translateY(-1px);
                }

                .officer-card--disabled {
                    cursor: not-allowed;
                    opacity: 0.6;
                }

                .officer-details {
                    display: flex;
                    flex-direction: column;
                    gap: 0.35rem;
                }

                .officer-name {
                    font-size: 1.25rem;
                    font-weight: 600;
                    color: #0f172a;
                }

                .officer-meta {
                    font-size: 0.95rem;
                    color: #52607a;
                }

                .officer-week {
                    font-size: 0.95rem;
                    color: #62708b;
                }

                .download-btn {
                    width: 48px;
                    height: 48px;
                    border-radius: 12px;
                    background: linear-gradient(135deg, #1c60ff, #0657d3);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    box-shadow: 0 15px 25px rgba(6, 87, 211, 0.25);
                    border: none;
                    transition: transform 0.15s ease, box-shadow 0.15s ease;
                }

                .download-btn:not(:disabled):hover {
                    transform: translateY(-2px);
                    box-shadow: 0 18px 28px rgba(6, 87, 211, 0.3);
                }

                .download-btn:disabled {
                    opacity: 0.6;
                    cursor: not-allowed;
                }

                .download-btn--loading {
                    background: #93c5fd;
                    box-shadow: none;
                }

                .loader {
                    width: 20px;
                    height: 20px;
                    border: 2px solid rgba(255, 255, 255, 0.5);
                    border-top-color: #fff;
                    border-radius: 9999px;
                    animation: spin 0.8s linear infinite;
                }

                .empty-state {
                    width: 100%;
                    text-align: center;
                    padding: 2rem 0;
                    color: #6b7280;
                    font-weight: 500;
                }

                @keyframes spin {
                    from {
                        transform: rotate(0deg);
                    }
                    to {
                        transform: rotate(360deg);
                    }
                }

                @media (max-width: 768px) {
                    .field-officer-report-wrapper {
                        padding: 0.5rem;
                    }

                    .field-officer-report-surface {
                        padding: 0;
                    }

                    .search-expansion {
                        flex-direction: column;
                        gap: 0.75rem;
                    }

                    .search-input {
                        width: 100%;
                        flex: none;
                    }

                    .search-close-btn {
                        width: 100%;
                        justify-content: center;
                    }

                    .officer-card {
                        flex-direction: column;
                        align-items: flex-start;
                    }

                    .download-btn {
                        align-self: flex-end;
                    }
                }
            `}</style>
        </div>
    );
};

export default FieldOfficerReport;
